import {
  getDefaultTooltipOption,
  optionBelongsToKind,
} from '@sb/integrations/utils';
import type { TCPOffsetOption } from '@sb/motion-planning';

import { type ValidationRule } from '../../types';
import {
  examplePlaceholderDiag,
  isExamplePlaceholder,
} from '../../util/examplePlaceholder';
import { createStepRule, diag } from '../../util/ruleHelpers';
import { isUnsetEquipmentRef } from '../../util/unsetEquipmentRef';

const SOURCE_RULE = 'steps.EnableGripper';

const MISSING_GRIPPER_MESSAGE =
  'Referenced gripper is not on this robot. Open the step to select a gripper.';

const NO_TOOLTIP_MESSAGE =
  'This gripper has no tooltip to set active. Open the step to update the tooltip setting.';

const STALE_OPTION_MESSAGE =
  "The selected tooltip doesn't belong to this gripper. Open the step to update the tooltip selection.";

/**
 * Checks for EnableGripper steps. A `deviceID` naming equipment this robot
 * does not have (a routine copied from another robot, a gripper deleted and
 * re-added) is reported, because the step would fail at play time. The
 * Active-Tooltip checks judge the tooltip fields against the selected
 * device, so they are skipped for an unresolved one and the fields stay
 * untouched for when the gripper returns. Nothing is ever rewritten.
 *
 * Intentionally function-style (not `CanonicalStepRuleBase`) so it adds only
 * these checks without retroactively subjecting EnableGripper steps to
 * runner-schema validation they were never checked against.
 */
export const EnableGripperValidation: ValidationRule = createStepRule({
  id: SOURCE_RULE,
  stepKinds: ['EnableGripper'],
  run: ({ routine, ctx, stepId }) => {
    const args = routine.stepConfigurations[stepId]?.args as
      | {
          deviceID?: string;
          setActiveTooltip?: boolean;
          activeTooltipOption?: TCPOffsetOption;
        }
      | undefined;

    const deviceID = args?.deviceID;

    // No selection yet — the wizard picks a gripper when the step is opened.
    if (isUnsetEquipmentRef(deviceID)) return [];

    // Before the exists-check: a copied example placeholder would otherwise
    // be reported as equipment that was removed from this robot.
    if (isExamplePlaceholder(deviceID)) {
      return [examplePlaceholderDiag(stepId, 'deviceID', SOURCE_RULE)];
    }

    // An absent list means equipment has not loaded, not that it is gone.
    if (ctx.equipment === undefined) return [];

    const device = ctx.equipment.find((eq) => eq.id === deviceID);

    if (!device) {
      return [
        diag({
          code: 'ui.missingEquipment',
          message: MISSING_GRIPPER_MESSAGE,
          stepId,
          sourceRule: SOURCE_RULE,
          severity: 'error',
        }),
      ];
    }

    // Flag-gated like the fields it judges: `RoutineContext.setActiveTooltip`
    // is a no-op while the flag is off, and the wizard renders neither the
    // toggle nor the picker there. Reporting would block a routine over a
    // setting that cannot take effect and cannot be edited on this robot.
    if (!ctx.featureFlags?.setActiveTooltip) return [];

    if (!args?.setActiveTooltip) return [];

    if (getDefaultTooltipOption(device.config) === undefined) {
      return [
        diag({
          code: 'ui.invalidConfiguration',
          message: NO_TOOLTIP_MESSAGE,
          stepId,
          sourceRule: SOURCE_RULE,
          severity: 'error',
        }),
      ];
    }

    if (
      args.activeTooltipOption !== undefined &&
      !optionBelongsToKind(args.activeTooltipOption, device.config.kind)
    ) {
      return [
        diag({
          code: 'ui.invalidConfiguration',
          message: STALE_OPTION_MESSAGE,
          stepId,
          sourceRule: SOURCE_RULE,
          severity: 'error',
        }),
      ];
    }

    return [];
  },
});
